import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Plus, Trash2, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEXTAREA_KEYS = new Set(['text', 'sub', 'detail', 'disclaimer', 'description', 'alt', 'tagline', 'paragraph', 'script']);
const IMAGE_RE = /^\/(assets|api\/site-assets)\//;
const isImageString = (v) => typeof v === 'string' && IMAGE_RE.test(v);
const isImageObject = (v) => v && typeof v === 'object' && !Array.isArray(v) && typeof v.full === 'string' && 'width' in v && 'height' in v;
export const pretty = (k) => k.replace(/([A-Z])/g, ' $1').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();

const inputCls = 'w-full rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#C92F4B]';
const labelCls = 'mb-1 block text-[11px] font-bold uppercase tracking-[0.12em] text-[#646A72]';

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API}/admin/assets`, { method: 'POST', body: fd, credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Upload failed');
  return data; // {url, width, height, path}
}

function ImageReplace({ value, onReplaced, compact }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true);
    setErr('');
    try {
      onReplaced(await uploadImage(f));
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };
  return (
    <div className="flex items-center gap-3">
      {!compact && value && <img src={value} alt="" className="h-16 w-auto rounded-lg border border-[#E4E6EB] bg-white object-contain" />}
      <button type="button" data-testid="image-replace-btn" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}
        className="flex items-center gap-2 rounded-full border border-[#E4E6EB] bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#404145] transition-colors hover:border-[#C92F4B] hover:text-[#C92F4B] disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {busy ? 'Uploading…' : 'Replace image'}
      </button>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} />
      {err && <span className="text-xs font-semibold text-[#A41225]">{err}</span>}
    </div>
  );
}

function PrimitiveField({ name, value, set }) {
  if (typeof value === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm text-[#404145]">
        <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} className="h-4 w-4 accent-[#C92F4B]" />
        <span className="font-semibold">{pretty(name)}</span>
      </label>
    );
  }
  if (typeof value === 'number') {
    return (
      <div>
        <span className={labelCls}>{pretty(name)}</span>
        <input type="number" value={value} onChange={(e) => set(Number(e.target.value))} className={inputCls} />
      </div>
    );
  }
  if (isImageString(value)) {
    return (
      <div>
        <span className={labelCls}>{pretty(name)}</span>
        <ImageReplace value={value} onReplaced={(r) => set(r.url)} />
      </div>
    );
  }
  const long = value.length > 70 || TEXTAREA_KEYS.has(name);
  return (
    <div>
      <span className={labelCls}>{pretty(name)}</span>
      {long
        ? <textarea rows={Math.min(6, Math.ceil(value.length / 90) + 1)} value={value} onChange={(e) => set(e.target.value)} className={inputCls} />
        : <input type="text" value={value} onChange={(e) => set(e.target.value)} className={inputCls} />}
    </div>
  );
}

function ImageObjectField({ name, value, set }) {
  const replace = (r) => set({ ...value, full: r.url, srcset: `${r.url} ${r.width}w`, width: r.width, height: r.height });
  return (
    <div className="rounded-xl border border-[#E4E6EB] bg-white p-3">
      <span className={labelCls}>{pretty(name)} (image)</span>
      <ImageReplace value={value.full} onReplaced={replace} />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div>
          <span className={labelCls}>Width</span>
          <input type="number" value={value.width} onChange={(e) => set({ ...value, width: Number(e.target.value) })} className={inputCls} />
        </div>
        <div>
          <span className={labelCls}>Height</span>
          <input type="number" value={value.height} onChange={(e) => set({ ...value, height: Number(e.target.value) })} className={inputCls} />
        </div>
      </div>
      {typeof value.alt === 'string' && (
        <div className="mt-2">
          <span className={labelCls}>Alt text</span>
          <input type="text" value={value.alt} onChange={(e) => set({ ...value, alt: e.target.value })} className={inputCls} />
        </div>
      )}
    </div>
  );
}

function StringArrayField({ name, value, set }) {
  return (
    <div>
      <span className={labelCls}>{pretty(name)} — one per line</span>
      <textarea rows={Math.max(3, value.length + 1)} value={value.join('\n')}
        onChange={(e) => set(e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
        className={inputCls} />
    </div>
  );
}

function JsonFallback({ name, value, set }) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [bad, setBad] = useState(false);
  return (
    <div>
      <span className={labelCls}>{pretty(name)} (JSON)</span>
      <textarea rows={6} value={text} className={`${inputCls} font-mono text-xs ${bad ? 'border-[#A41225]' : ''}`}
        onChange={(e) => {
          setText(e.target.value);
          try { set(JSON.parse(e.target.value)); setBad(false); } catch { setBad(true); }
        }} />
      {bad && <span className="text-xs font-semibold text-[#A41225]">Invalid JSON — fix to apply</span>}
    </div>
  );
}

// Second-level renderer for fields inside array items / nested scene objects — no recursion.
function SubField({ name, value, path, onChange }) {
  const set = (v) => onChange(path, v);
  if (value === null || ['boolean', 'number', 'string'].includes(typeof value)) {
    return <PrimitiveField name={name} value={value} set={set} />;
  }
  if (isImageObject(value)) return <ImageObjectField name={name} value={value} set={set} />;
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    return <StringArrayField name={name} value={value} set={set} />;
  }
  return <JsonFallback name={name} value={value} set={set} />;
}

function ArrayItemCard({ item, index, path, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const title = (item && (item.label || item.title || item.id || item.name)) || `Item ${index + 1}`;
  return (
    <div className="rounded-xl border border-[#E4E6EB] bg-white">
      <div className="flex items-center justify-between px-3 py-2">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-xs font-bold text-[#1C1D21]">
          <ChevronDown size={14} className={`transition-transform ${open ? '' : '-rotate-90'}`} /> {title}
        </button>
        <button type="button" data-testid={`remove-item-${index}`} onClick={onRemove} aria-label={`Remove ${title}`}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#A7ABB0] hover:bg-[#A41225]/10 hover:text-[#A41225]">
          <Trash2 size={14} />
        </button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-[#E4E6EB] p-3">
          {Object.entries(item).map(([k, v]) => (
            <SubField key={k} name={k} value={v} path={[...path, k]} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
}

function ObjectArrayField({ name, value, path, onChange }) {
  const set = (v) => onChange(path, v);
  return (
    <div>
      <span className={labelCls}>{pretty(name)} ({value.length} items)</span>
      <div className="space-y-3">
        {value.map((item, i) => (
          <ArrayItemCard key={i} item={item} index={i} path={[...path, i]} onChange={onChange}
            onRemove={() => set(value.filter((_, j) => j !== i))} />
        ))}
        <button type="button" data-testid={`add-item-${name}`}
          onClick={() => set([...value, JSON.parse(JSON.stringify(value[value.length - 1] || {}))])}
          className="flex items-center gap-1.5 rounded-full border border-dashed border-[#A7ABB0] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#646A72] hover:border-[#C92F4B] hover:text-[#C92F4B]">
          <Plus size={13} /> Add item
        </button>
      </div>
    </div>
  );
}

// One-level plain object (e.g. JOURNEY scenes) — renders each key via SubField, no recursion.
function FlatObjectField({ name, value, path, onChange }) {
  return (
    <fieldset className="rounded-xl border border-[#E4E6EB] bg-white/60 p-3">
      <legend className="px-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#646A72]">{pretty(name)}</legend>
      <div className="space-y-3">
        {Object.entries(value).map(([k, v]) => (
          <SubField key={k} name={k} value={v} path={[...path, k]} onChange={onChange} />
        ))}
      </div>
    </fieldset>
  );
}

// Top-level dispatcher — unrolled, zero recursive JSX (the visual-edits babel plugin
// stack-overflows on mutually recursive JSX components).
export function Field({ name, value, path, onChange }) {
  if (value === null || ['boolean', 'number', 'string'].includes(typeof value)) {
    return <PrimitiveField name={name} value={value} set={(v) => onChange(path, v)} />;
  }
  if (isImageObject(value)) return <ImageObjectField name={name} value={value} set={(v) => onChange(path, v)} />;
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === 'string')) {
      return <StringArrayField name={name} value={value} set={(v) => onChange(path, v)} />;
    }
    return <ObjectArrayField name={name} value={value} path={path} onChange={onChange} />;
  }
  if (value && typeof value === 'object') {
    return <FlatObjectField name={name} value={value} path={path} onChange={onChange} />;
  }
  return null;
}
